import { produce } from 'immer'
import { BulkVirtualCardJobStatus } from 'src/models/virtualCard.models'
import {
  cspIssuersQueryKeys,
  expensesQueryKeys,
  OOPE_QK,
  OOPE_REPORT_QK,
  ORG_USAGES_QK,
  userQueryKeys,
  virtualCardsQueryKeys,
} from 'src/queries/queries.constants'
import { queryClient } from 'src/queryClient'
import {
  GET_CSP_ME_SUCCESS,
  GET_ME_SUCCESS,
  POST_CSP_LOGIN_ISSUER_INFO_SUCCESS,
  POST_CSP_LOGIN_SUCCESS,
} from 'src/redux/actions/auth.actions'
import {
  DELETE_EXPENSE_SUCCESS,
  PATCH_EXPENSE_SUCCESS,
  PATCH_REIMBURSEMENT_MANAGER_APPROVAL,
  PATCH_UPDATE_EXPENSE_REPORT_SUCCESS,
  PatchReimbursementManagerApprovalActions,
  PatchUpdateExpenseReportActions,
  POST_APPROVE_EXPENSES_SUCCESS,
  POST_CREATE_EXPENSE_SUCCESS,
  POST_MARK_EXPENSE_REPORTS_PAID_SUCCESS,
  POST_REJECT_EXPENSES_SUCCESS,
  POST_SUBMIT_EXPENSE_REPORT_SUCCESS,
  PostApproveExpensesActions,
  PostRejectExpensesActions,
  PostSubmitExpenseReportActions,
} from 'src/redux/actions/data/expenses.actions'
import {
  PATCH_USER_SUCCESS,
  POST_USER_AVATAR_SUCCESS,
} from 'src/redux/actions/data/users.actions'
import {
  GET_BULK_VIRTUAL_CARD_JOB_STATUS_SUCCESS,
  GET_VIRTUAL_CARD_SUCCESS,
  POST_VIRTUAL_CARD_SUCCESS,
  PUT_CLOSE_VIRTUAL_CARD_SUCCESS,
  PUT_VIRTUAL_CARD_CANCEL_SUCCESS,
  PUT_VIRTUAL_CARD_CANCEL_UPDATE_SUCCESS,
  PUT_VIRTUAL_CARD_REJECT_SUCCESS,
  PUT_VIRTUAL_CARD_SUCCESS,
} from 'src/redux/actions/data/virtualCards.actions'
import { Actions } from 'src/redux/reducers/data.reducer'

/**
 * Every dispatched action flows through this reducer at runtime, but the data
 * reducer's `Actions` union only covers `data/*` reducers. The OOPE
 * approve/reject/submit/rename/manager-approval mutations live in the
 * ExpenseReport feature reducer, so widen the accepted type to react to them
 * here (the EX-39746 reads slice migrated the OOPE report-detail read to React
 * Query; these surviving redux mutations invalidate it through this reducer).
 */
type InteropActions =
  | Actions
  | PostApproveExpensesActions
  | PostRejectExpensesActions
  | PostSubmitExpenseReportActions
  | PatchUpdateExpenseReportActions
  | PatchReimbursementManagerApprovalActions

const ReactQueryInteropReducer = (
  state: Record<string, never> = {},
  action: InteropActions,
): Record<string, never> => {
  return produce(state, (_) => {
    switch (action.type) {
      case PUT_VIRTUAL_CARD_CANCEL_SUCCESS:
      case PUT_CLOSE_VIRTUAL_CARD_SUCCESS:
      case PUT_VIRTUAL_CARD_CANCEL_UPDATE_SUCCESS:
      case PUT_VIRTUAL_CARD_REJECT_SUCCESS:
      case PUT_VIRTUAL_CARD_SUCCESS:
      case POST_VIRTUAL_CARD_SUCCESS: {
        // TODO: we will need to save the resulting virtual card to the query store manually
        // because of timing issues with SpiceDB when attempting to immediately refetch paginated views
        queryClient.invalidateQueries({ queryKey: virtualCardsQueryKeys.all })
        // Active virtual-card count drives SaaS limit gating — refetch after any
        // VC create/close/cancel so the downgrade meter sees the latest count.
        // Only invalidate when an org-usages query is already cached (i.e. the
        // user is on a SaaS-enabled brand and a consumer has mounted it).
        // Without this gate, non-SaaS brands trigger a wasted /saas/{orgId}/usages
        // refetch (the endpoint 404s) on every VC mutation app-wide.
        const usagesAlreadyCached = queryClient
          .getQueryCache()
          .findAll({
            predicate: (q) => q.queryKey.includes(ORG_USAGES_QK),
          })
          .some((q) => q.state.data !== undefined)
        if (usagesAlreadyCached) {
          queryClient.invalidateQueries({
            predicate: (query) => query.queryKey.includes(ORG_USAGES_QK),
          })
        }
        break
      }
      case GET_BULK_VIRTUAL_CARD_JOB_STATUS_SUCCESS: {
        if (action.payload.status === BulkVirtualCardJobStatus.Completed) {
          queryClient.invalidateQueries({ queryKey: virtualCardsQueryKeys.all })
        }
        break
      }
      case GET_VIRTUAL_CARD_SUCCESS: {
        // The detail surface now reads the card/permissions from React Query, so
        // the legacy getVirtualCard refresh (Pusher VirtualCard events,
        // sanctioned-screening completion) must invalidate the RQ detail cache —
        // otherwise the mounted detail page renders stale state until a hard
        // refresh. Skip when the action came from the detail query's own
        // onSuccess to avoid a refetch loop. Invalidating detail(id) also covers
        // its permissions/revisions queries (they share the detail key prefix).
        if (action.meta?.source !== 'react-query') {
          const virtualCardId = action.payload?.virtualCard?.id
          if (virtualCardId) {
            queryClient.invalidateQueries({
              queryKey: virtualCardsQueryKeys.detail(virtualCardId),
            })
          }
        }
        break
      }
      case POST_APPROVE_EXPENSES_SUCCESS:
      case POST_REJECT_EXPENSES_SUCCESS:
      case POST_SUBMIT_EXPENSE_REPORT_SUCCESS:
      case POST_MARK_EXPENSE_REPORTS_PAID_SUCCESS:
      case DELETE_EXPENSE_SUCCESS:
      case PATCH_UPDATE_EXPENSE_REPORT_SUCCESS:
      case PATCH_REIMBURSEMENT_MANAGER_APPROVAL:
      case POST_CREATE_EXPENSE_SUCCESS:
      case PATCH_EXPENSE_SUCCESS: {
        // EX-39746: the OOPE report-detail read (GET /expensereports/:id) is now
        // served by React Query (oopeQueryKeys.reportDetails). These OOPE
        // mutations stay redux thunks for a stacked follow-up; before the
        // migration they refreshed the report by re-dispatching getExpenseReport
        // (or mutating the feature slice in place). Invalidate the report-detail
        // key prefix so the open report refetches the server's truth. React
        // Query keeps the previous data while refetching, so there is no loading
        // flash; only an extra round-trip vs. the old in-place update.
        queryClient.invalidateQueries({ queryKey: [OOPE_QK, OOPE_REPORT_QK] })
        // Single-expense reads (GET /expenses/:id) are also React Query now;
        // approve/reject/patch change an expense's fields, so refresh any cached
        // expense detail too.
        queryClient.invalidateQueries({ queryKey: expensesQueryKeys.all() })
        break
      }
      case PATCH_USER_SUCCESS: {
        queryClient.invalidateQueries({ queryKey: userQueryKeys.all })
        break
      }
      case POST_USER_AVATAR_SUCCESS: {
        queryClient.invalidateQueries({ queryKey: userQueryKeys.me() })
        break
      }
      case GET_ME_SUCCESS: {
        // Only invalidate React Query cache when the action comes from Redux
        // (not from React Query's own onSuccess callback, which already has fresh data)
        if (action.meta?.source !== 'react-query') {
          queryClient.invalidateQueries({ queryKey: userQueryKeys.me() })
        }
        break
      }
      case GET_CSP_ME_SUCCESS: {
        queryClient.invalidateQueries({ queryKey: userQueryKeys.me() })
        // Seed (not invalidate) the CSP issuers cache from the thunk's
        // payload: the getCspMe thunk remains the auth-boundary fetcher
        // (SSR seed, App.tsx CSP-auth effect) and seeding preserves the
        // legacy single-fetch behavior for state.data.issuers readers.
        queryClient.setQueryData(cspIssuersQueryKeys.me(), action.payload)
        break
      }
      case POST_CSP_LOGIN_SUCCESS:
      case POST_CSP_LOGIN_ISSUER_INFO_SUCCESS: {
        // The login payload embeds the same user+issuers as /csp/me; seeding
        // avoids an extra /csp/me fetch the moment a CSP-gated query mounts.
        queryClient.setQueryData(cspIssuersQueryKeys.me(), {
          user: action.payload.user,
          issuers: action.payload.issuers,
        })
        break
      }
    }
  })
}

export default ReactQueryInteropReducer
